import { useAuthContext } from '@core/shared/src/components/AuthContext';
import { OptionsApi, WebinarContentSummary } from '@core/swagger-client';
import { AccountVerificationRequired, ContainerDefault, ErrorMessagePage, Heading, Loader, LoaderPage, SearchInput, TabGroup } from '@core/ui';
import { getRouteApi, useNavigate, useRememberPage, useShowWebinar } from '@online-webinar/router';
import { Link } from '@online-webinar/ui-frontend';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WebinarListContentLoader } from '../../atoms';
import { ListWebinarCard } from '../../molecules';
import { UpNextWebinar } from '../../organisms';

const route = getRouteApi('/organized/$tab');
export const ListWebinars = () => {
    const { t } = useTranslation();
    const [search, setSearch] = useState<string>('');
    const { isVerified } = useAuthContext();

    const navigate = useNavigate();
    const loaderCount = [1, 2, 3, 4, 5, 6, 7, 8, 9];

    const { tab } = route.useParams();
    const tabs = ['upcoming', 'ended'];
    const activeTabIndex = tabs.indexOf(tab || '');
    const changeTab = (tabIndex: number) => {
        navigate({ to: '/organized/$tab', params: { tab: tabs[tabIndex] } });
    };
    useRememberPage('overview', `/organized/${tab}`);

    const {
        isLoading: isUpcomingWebinarsLoading,
        data: upcomingWebinars,
        error: upcomingWebinarsError
    } = useQuery(
        OptionsApi.webinars.getMyUpcoming({
            refetchInterval: 1000 * 60 * 1,
            enabled: activeTabIndex === 0 || !!search
        })
    );

    const {
        isLoading: isEndedWebinarsLoading,
        data: endedWebinars,
        error: endedWebinarsError
    } = useQuery(
        OptionsApi.webinars.getMyEnded({
            refetchInterval: 1000 * 60 * 1,
            enabled: activeTabIndex === 1 || !!search
        })
    );

    useShowWebinar(!!(activeTabIndex === 0 ? upcomingWebinars : endedWebinars));

    if (activeTabIndex === -1) {
        navigate({ to: '/organized/$tab', params: { tab: tabs[0] } });
        return <LoaderPage />;
    }

    // TODO: implement fetching method with pagination

    const error = upcomingWebinarsError ?? endedWebinarsError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    const webinarFilter = (webinar: WebinarContentSummary) => {
        if (
            search &&
            !webinar.webinarId.toLowerCase().includes(search.toLowerCase()) &&
            !webinar.title.toLowerCase().includes(search.toLowerCase()) &&
            !webinar.description.toLowerCase().includes(search.toLowerCase()) &&
            !webinar.tags.some((tag) => tag.toLowerCase().includes(search.toLowerCase())) &&
            !webinar.nameOfHost.toLowerCase().includes(search.toLowerCase())
        ) {
            if (!webinar.clientReference || !webinar.clientReference.toLowerCase().includes(search.toLowerCase())) {
                return false;
            }
        }

        return true;
    };

    const webinars = [...(upcomingWebinars || []), ...(endedWebinars || [])].filter((webinar) => webinarFilter(webinar));
    return (
        <ContainerDefault>
            <div data-testid="webinarsList" className="max-w-300">
                <Heading variant="h1" classNames="mb-8 md:mb-10">
                    {t('Pages.Organized.Header')}
                </Heading>
                <SearchInput classNames="mb-6" onSearch={(value) => setSearch(value)} />
                <TabGroup direction={TabGroup.direction.HORIZONTAL}>
                    <TabGroup.TabList numTabs={2} activeTab={activeTabIndex} setActiveTab={changeTab} classNames="border-b border-primary flex justify-center">
                        {!search && (
                            <>
                                <TabGroup.Tab
                                    activeTab={activeTabIndex}
                                    setActiveTab={changeTab}
                                    index={0}
                                    className="nav-tab-border"
                                    activeClassName="nav-tab--active"
                                >
                                    {t('Pages.Organized.TabUpcoming')}
                                </TabGroup.Tab>
                                <TabGroup.Tab
                                    activeTab={activeTabIndex}
                                    setActiveTab={changeTab}
                                    index={1}
                                    className="nav-tab-border"
                                    activeClassName="nav-tab--active"
                                >
                                    {t('Pages.Organized.TabEnded')}
                                </TabGroup.Tab>
                            </>
                        )}
                        {!!search && (
                            <TabGroup.Tab activeTab={3} setActiveTab={() => {}} index={3} className="nav-tab-border" activeClassName="nav-tab--active">
                                {t('Pages.Organized.TabSearch')}
                            </TabGroup.Tab>
                        )}
                    </TabGroup.TabList>
                    {!search && (
                        <>
                            <TabGroup.TabPanel
                                activeTab={activeTabIndex}
                                setActiveTab={changeTab}
                                index={0}
                                className="grid gap-4 md:gap-6 mt-6 md:mt-8 -mx-2 xs:mx-0 transition-all transform"
                                activeClassName="opacity-100 duration-500 translate-x-0"
                                inactiveClassName="absolute left-0 right-0 top-12 opacity-0 -translate-x-2 h-0 overflow-hidden"
                            >
                                {!!upcomingWebinars && upcomingWebinars.length > 0 && !isVerified && (
                                    <div className="px-4 pt-4 md:px-8">
                                        <AccountVerificationRequired />
                                    </div>
                                )}
                                {!!upcomingWebinars && upcomingWebinars.length === 0 && (
                                    <>
                                        <Heading variant="h2" classNames="mb-2">
                                            {t('Pages.Organized.NoUpcoming.Title')}
                                        </Heading>
                                        <p className="mb-10">{t('Pages.Organized.NoUpcoming.Text')}</p>
                                        <Link to="/create" variant="primary" classNames="justify-self-start">
                                            {t('Pages.Organized.NoUpcoming.Button')}
                                        </Link>
                                    </>
                                )}
                                {!!upcomingWebinars && upcomingWebinars.length > 0 && (
                                    <>
                                        <Heading variant="h2" classNames="sr-only">
                                            {t('Pages.Organized.Heading.Upcoming')}
                                        </Heading>
                                        <UpNextWebinar upcomingWebinar={upcomingWebinars[0]} />
                                        {upcomingWebinars.slice(1).map((webinar) => (
                                            <ListWebinarCard key={webinar.webinarId} webinar={webinar} />
                                        ))}
                                    </>
                                )}
                                {(isUpcomingWebinarsLoading || isEndedWebinarsLoading) && <WebinarListContentLoader loaderCount={loaderCount} />}
                            </TabGroup.TabPanel>
                            <TabGroup.TabPanel
                                activeTab={activeTabIndex}
                                setActiveTab={changeTab}
                                index={1}
                                className="grid gap-4 md:gap-6 mt-6 md:mt-8 -mx-2 xs:mx-0 transition-all transform"
                                activeClassName="opacity-100 duration-500 translate-x-0"
                                inactiveClassName="absolute left-0 right-0 top-12 opacity-0 -translate-x-2 h-0 overflow-hidden"
                            >
                                {!!endedWebinars && endedWebinars.length === 0 && (
                                    <>
                                        <Heading variant="h2" classNames="mb-2">
                                            {t('Pages.Organized.NoEnded.Title')}
                                        </Heading>
                                        <p className="mb-10">{t('Pages.Organized.NoEnded.Text')}</p>
                                    </>
                                )}
                                {!!endedWebinars && endedWebinars.length > 0 && (
                                    <>
                                        <Heading variant="h2" classNames="sr-only">
                                            {t('Pages.Organized.Heading.OnDemand')}
                                        </Heading>
                                        {endedWebinars.map((webinar) => (
                                            <ListWebinarCard key={webinar.webinarId} webinar={webinar} />
                                        ))}
                                    </>
                                )}
                            </TabGroup.TabPanel>
                        </>
                    )}
                    {!!search && (
                        <TabGroup.TabPanel
                            activeTab={3}
                            setActiveTab={() => {}}
                            index={3}
                            className="grid gap-4 md:gap-6 mt-6 md:mt-8 -mx-2 xs:mx-0 transition-all transform"
                            activeClassName="opacity-100 duration-500 translate-x-0"
                            inactiveClassName="absolute left-0 right-0 top-12 opacity-0 -translate-x-2 h-0 overflow-hidden"
                        >
                            {!endedWebinars && !upcomingWebinars && <Loader />}
                            {!!endedWebinars && !!upcomingWebinars && webinars.length === 0 && (
                                <>
                                    <Heading variant="h2" classNames="mb-2">
                                        {t('Pages.Organized.NotFound.Title')}
                                    </Heading>
                                    <p className="mb-10">{t('Pages.Organized.NotFound.Text')}</p>
                                </>
                            )}
                            {!!endedWebinars && !!upcomingWebinars && webinars.length > 0 && (
                                <>
                                    <Heading variant="h2" classNames="sr-only">
                                        {t('Pages.Organized.Heading.Found')}
                                    </Heading>
                                    {webinars.map((webinar) => (
                                        <ListWebinarCard key={webinar.webinarId} webinar={webinar} />
                                    ))}
                                </>
                            )}
                        </TabGroup.TabPanel>
                    )}
                </TabGroup>
            </div>
        </ContainerDefault>
    );
};
